import React, { memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react';
// Mock data - in a real app this would come from an API based on the ID
const blogPost = {
  id: 1,
  title: 'The Future of Digital Brutalism in 2024',
  excerpt: 'Exploring how raw aesthetics and bold typography are reshaping the modern web landscape.',
  category: 'Design',
  author: 'Alex Chen',
  date: 'Mar 15, 2024',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1558655146-d09347e0b7a9?auto=format&fit=crop&w=1600&q=80',
  content: `
    <p>The web is swinging back to the raw, the unpolished, and the bold. Digital Brutalism, a style characterized by its ruggedness and lack of concern to look comfortable or easy, has been gaining traction over the last few years. But in 2024, it's evolving into something new: Refined Brutalism.</p>
    
    <h3>The Origins of the Aesthetic</h3>
    <p>Originating from the architectural style of the 1950s that emphasized raw concrete (béton brut) and structural visibility, digital brutalism translates these concepts into web design through system fonts, high-contrast colors, and visible grid lines.</p>
    
    <blockquote>"Brutalism is not about being ugly. It's about being honest with your materials and construction."</blockquote>
    
    <p>In the digital realm, this means exposing the code, using default browser styles, and rejecting the overly polished, "corporate Memphis" style that dominated the late 2010s. It's a reaction against the homogenization of the web.</p>
    
    <h3>Why It Resonates Now</h3>
    <p>Users are tired of perfect, cookie-cutter templates. They crave authenticity and personality. A brutalist website says, "We are confident enough to be raw." It stands out in a sea of gradients and rounded corners.</p>
    
    <ul>
      <li><strong>High Contrast:</strong> Black and white, or neon on dark backgrounds.</li>
      <li><strong>Typography First:</strong> Large, imposing fonts that act as the main visual element.</li>
      <li><strong>Micro-interactions:</strong> Sharp, instant hover effects rather than smooth, slow transitions.</li>
    </ul>
    
    <h3>Implementing the Style</h3>
    <p>To adopt this style without sacrificing usability, focus on hierarchy. The chaos should be controlled. Use the grid to your advantage—break it intentionally, not accidentally. Ensure that despite the raw look, the user experience remains intuitive.</p>
    
    <p>At KUJODO, we believe that bold design choices like these are what separate memorable brands from forgettable ones. It's not for everyone, but that's exactly the point.</p>
  `
};
export function BlogDetail() {
  const {
    id
  } = useParams();
  // In a real app, fetch post by id here
  // const post = fetchPost(id)
  return <div className="min-h-screen bg-black text-white selection:bg-yellow-400 selection:text-black" data-id="element-143">
      <Navigation data-id="element-144" />

      <main className="pt-32 pb-24" data-id="element-145">
        {/* Hero Image */}
        <div className="w-full h-[50vh] md:h-[70vh] relative overflow-hidden" data-id="element-146">
          <img src={blogPost.image} alt={blogPost.title} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" data-id="element-147" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" data-id="element-148" />
        </div>

        <div className="max-w-4xl mx-auto px-6 -mt-32 relative z-10" data-id="element-149">
          <div className="bg-black border border-white/10 p-8 md:p-12" data-id="element-150">
            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm font-bold uppercase tracking-wider text-gray-400" data-id="element-151">
              <Link to="/blog" className="flex items-center hover:text-yellow-400 transition-colors" data-id="element-152">
                <ArrowLeft className="w-4 h-4 mr-2" data-id="element-153" /> Back to Blog
              </Link>
              <span className="bg-yellow-400 text-black px-3 py-1" data-id="element-154">
                {blogPost.category}
              </span>
              <span className="flex items-center" data-id="element-155">
                <Calendar className="w-4 h-4 mr-2" data-id="element-156" /> {blogPost.date}
              </span>
              <span className="flex items-center" data-id="element-157">
                <Clock className="w-4 h-4 mr-2" data-id="element-158" /> {blogPost.readTime}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight uppercase tracking-tighter" data-id="element-159">
              {blogPost.title}
            </h1>

            {/* Author */}
            <div className="flex items-center justify-between border-b border-white/10 pb-8 mb-12" data-id="element-160">
              <div className="flex items-center" data-id="element-161">
                <div className="w-12 h-12 bg-gray-800 rounded-full mr-4 overflow-hidden" data-id="element-162">
                  {/* Placeholder avatar */}
                  <div className="w-full h-full bg-yellow-400 flex items-center justify-center text-black font-bold text-xl" data-id="element-163">
                    {blogPost.author.charAt(0)}
                  </div>
                </div>
                <div data-id="element-164">
                  <p className="text-white font-bold uppercase tracking-wider" data-id="element-165">
                    Written by
                  </p>
                  <p className="text-gray-400" data-id="element-166">{blogPost.author}</p>
                </div>
              </div>

              <button className="text-gray-400 hover:text-yellow-400 transition-colors" data-id="element-167">
                <Share2 className="w-6 h-6" data-id="element-168" />
              </button>
            </div>

            {/* Content */}
            <div className="prose prose-invert prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tighter prose-a:text-yellow-400 prose-blockquote:border-l-yellow-400 prose-blockquote:text-gray-300 prose-img:rounded-none" data-id="element-169">
              <div dangerouslySetInnerHTML={{
              __html: blogPost.content
            }} data-id="element-170" />
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-171" />
    </div>;
}